import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { RemoteV2State, TopicData, LogEntry } from '../types/remoteCurrentTypes';
import { fetchModalConfig } from '../services/remoteService';

const initialState: RemoteV2State = {
  topicData: {},
  connectionStatus: 'Disconnected',
  logs: [],
  availableTopics: [],
  loadingTopics: false,
  apiSiteId: undefined,
  modalConfig: null,
  modalConfigLoading: false,
  modalConfigError: null,
};

const remoteV2Slice = createSlice({
  name: 'remoteV2',
  initialState,
  reducers: {
    updateTopicData: (state, action: PayloadAction<Record<string, TopicData>>) => {
      state.topicData = action.payload;
    },
    setConnectionStatus: (state, action: PayloadAction<string>) => {
      state.connectionStatus = action.payload;
    },
    addLog: (state, action: PayloadAction<LogEntry>) => {
      state.logs.push(action.payload);
      if (state.logs.length > 100) {
        state.logs = state.logs.slice(-100);
      }
    },
    clearLogs: (state) => {
      state.logs = [];
    },
    setAvailableTopics: (state, action: PayloadAction<string[]>) => {
      state.availableTopics = action.payload;
    },
    setLoadingTopics: (state, action: PayloadAction<boolean>) => {
      state.loadingTopics = action.payload;
    },
    clearTopicData: (state) => {
      state.topicData = {};
    },
    setApiSiteId: (state, action: PayloadAction<string | undefined>) => {
      state.apiSiteId = action.payload;
    },
    resetRemoteV2State: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchModalConfig.pending, (state) => {
        state.modalConfigLoading = true;
        state.modalConfigError = null;
      })
      .addCase(fetchModalConfig.fulfilled, (state, action) => {
        state.modalConfigLoading = false;
        state.modalConfig = action.payload;
        state.modalConfigError = null;
      })
      .addCase(fetchModalConfig.rejected, (state, action) => {
        state.modalConfigLoading = false;
        state.modalConfigError = action.payload || 'Failed to fetch modal configuration';
      });
  }
});

export const {
  updateTopicData,
  setConnectionStatus,
  addLog,
  clearLogs,
  setAvailableTopics,
  setLoadingTopics,
  clearTopicData,
  setApiSiteId,
  resetRemoteV2State
} = remoteV2Slice.actions;

export default remoteV2Slice.reducer;